/**
 * Wave-69: catch-all bookmark-migration component.
 *
 * The router moved the entire authenticated tree under `/:shop_slug/`
 * (so `/vendors/kernvel-vendoriq/dashboard` instead of the prior
 * `/vendors/dashboard`). Vendors who bookmarked the slug-less URLs
 * would 404 without this fallback.
 *
 * Behaviour:
 *   1. Read the original pathname (e.g. `/dashboard` or `/orders/123`).
 *   2. Look up the remembered shop_slug from localStorage (set by
 *      the AuthContext on every successful login/register).
 *   3. If found, hard-redirect to `/<slug>/<original_path>` —
 *      preserves the deep-link target so a vendor who bookmarked
 *      `/orders/abc-123` lands back on that exact order page.
 *   4. If no remembered slug, send to the bare login (which itself
 *      renders the "No marketplace selected" amber banner).
 *
 * The basename `/vendors` is applied by react-router, so paths here
 * are written without it (matching the router's internal model).
 */
import { Navigate, useLocation } from 'react-router-dom';
import { getRememberedShopSlug, normalizeInternalPath, scopedPath } from '../utils/shopSlug';

export function SlugRecover() {
  const location = useLocation();
  const remembered = getRememberedShopSlug();

  // The path here doesn't include `/vendors` basename — it's already
  // stripped by react-router. So `location.pathname` is e.g.
  // `/dashboard` or `/orders/abc-123`. We want to redirect to
  // `/<remembered>/dashboard` etc.
  const query = location.search || '';
  const hash = location.hash || '';
  const safeOriginal = normalizeInternalPath(
    `${location.pathname}${query}${hash}`,
    '/dashboard',
  );
  const original = safeOriginal.split(/[?#]/, 1)[0].replace(/^\/+/, '');

  // No memory of which marketplace — best we can do is send them to
  // the bare /login. Login.tsx renders the "No marketplace selected"
  // banner explaining the situation. Better than a 404.
  if (!remembered) {
    return <Navigate to={normalizeInternalPath(`/login${query}${hash}`, '/login')} replace />;
  }

  // Don't get into a redirect loop if the original path already had
  // a slug (shouldn't happen because the router shape ensures
  // `/:shop_slug/anything` is matched by the protected tree, not
  // the catch-all — but belt + suspenders).
  if (original.startsWith(`${remembered}/`) || original === remembered) {
    return <Navigate to={safeOriginal} replace />;
  }

  // Common case — bookmark from before the slug refactor. Stitch the
  // slug in front and preserve the rest of the URL.
  return <Navigate to={scopedPath(safeOriginal)} replace />;
}
